import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui';
import './ErrorBoundary.css';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child components and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Suppress abort errors - don't show error UI for these
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      return { hasError: false };
    }
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Suppress abort errors completely
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      return;
    }

    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);

    // You can also log to an error reporting service here
    this.setState({
      error,
      errorInfo
    });

    // Optional: Send to error tracking service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <AlertTriangle size={64} />
            </div>

            <h1 className="error-boundary-title">Oops! Something went wrong</h1>

            <p className="error-boundary-message">
              We're sorry for the inconvenience. The application encountered an unexpected error.
            </p>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary-stack">
                  <p><strong>Error:</strong> {this.state.error.toString()}</p>
                  {this.state.errorInfo && (
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  )}
                </div>
              </details>
            )}

            <div className="error-boundary-actions">
              <Button
                variant="primary"
                leftIcon={<RefreshCw size={18} />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>

              <Button
                variant="outline"
                leftIcon={<Home size={18} />}
                onClick={this.handleGoHome}
              >
                Go to Home
              </Button>
            </div>

            <p className="error-boundary-support">
              If this problem persists, please contact support at{' '}
              <a href="mailto:admin@wecarefinserv.com">admin@wecarefinserv.com</a>
            </p>
          </div>
        </div>
      );
    }

    // Render children if no error
    return this.props.children;
  }
}

export default ErrorBoundary;
